import { lineTotal, safeNumber } from "@/lib/quoteNumbers";
// Full-screen quote line item workspace — /app/quotes/:qid/items
// Desktop-only alternate editing view over the SAME quote document/state as
// QuoteWorkspace.jsx (shared via useQuoteEditor). No parallel data model,
// no separate save path — every edit here flows through the same autosave.
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Check, ChevronDown, ChevronRight, Copy, GripVertical, Hammer, Layers, Library,
  Loader2, Minimize2, MoreHorizontal, Package, Plus, Search, Trash2, Truck, X,
} from "lucide-react";
import { toast } from "sonner";
import { useQuoteEditor, isNewDraft, STATUS_STYLE, nzd } from "@/hooks/useQuoteEditor";
import { ItemLibraryDialog } from "@/components/QuoteWorkspaceParts";

const VIEWS = [
  { key: "sell", label: "Sell" },
  { key: "costing", label: "Costing" },
  { key: "margin", label: "Margin" },
];
const CATEGORIES = ["labour", "material", "plant", "service"];
const cellIn = "h-8 rounded-sm border-zinc-200 dark:border-white/10 dark:bg-white/5 text-xs px-2";

const costTotal = (it) => safeNumber(it.labour_cost) + safeNumber(it.material_cost) + safeNumber(it.plant_cost);

export default function QuoteItemWorkspace() {
  const { qid } = useParams();
  const navigate = useNavigate();
  const {
    q, loading, saveState, totals, items, updateItem, removeItem, duplicateItem,
    setItems, addItem, addSection, dragRef, onDrop, sectionSubtotals,
  } = useQuoteEditor(qid);

  const [view, setView] = useState("sell");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [bulkPct, setBulkPct] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkCostCode, setBulkCostCode] = useState("");
  const tableRef = useRef(null);

  if (loading || !q) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  }

  const status = q.status || "draft";
  const editable = !["accepted", "declined", "converted"].includes(status);

  const matchesFilter = (it) => {
    if (catFilter !== "all" && (it.category || "") !== catFilter) return false;
    if (!search.trim()) return true;
    const q2 = search.trim().toLowerCase();
    return [it.description, it.detail, it.cost_code, it.category].filter(Boolean).join(" ").toLowerCase().includes(q2);
  };

  // visible = section headers always shown (unless collapsed); item rows filtered by search/category
  const hasActiveFilter = catFilter !== "all" || !!search.trim();
  let collapsedSection = false;
  const visible = items
    .map((it, i) => {
      if (it.is_section) { collapsedSection = !!it.collapsed; return { it, i, hidden: false }; }
      const hidden = collapsedSection || (hasActiveFilter && !matchesFilter(it));
      return { it, i, hidden };
    })
    .filter(({ hidden }) => !hidden);

  const toggleSelect = (i) => setSelected((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const clearSelection = () => setSelected(new Set());

  const applyBulk = (fn) => { setItems(items.map((it, i) => (selected.has(i) && !it.is_section ? fn(it) : it))); };
  const bulkMarkup = () => {
    const pct = Number(bulkPct);
    if (!pct && pct !== 0) return toast.error("Enter a % first");
    applyBulk((it) => ({ ...it, unit_price: +((it.unit_price || 0) * (1 + pct / 100)).toFixed(2) }));
    toast.success(`Sell price adjusted ${pct > 0 ? "+" : ""}${pct}% on ${selected.size} row(s)`);
  };
  const bulkDiscount = () => {
    const pct = Number(bulkPct);
    if (!bulkPct) return toast.error("Enter a % first");
    applyBulk((it) => ({ ...it, discount_pct: Math.min(100, Math.max(0, pct)) }));
    toast.success(`Discount set to ${pct}% on ${selected.size} row(s)`);
  };
  const bulkApplyCategory = () => {
    if (!bulkCategory) return toast.error("Pick a category first");
    applyBulk((it) => ({ ...it, category: bulkCategory }));
    toast.success(`Category set on ${selected.size} row(s)`);
  };
  const bulkApplyCostCode = () => {
    if (!bulkCostCode.trim()) return toast.error("Enter a cost code first");
    applyBulk((it) => ({ ...it, cost_code: bulkCostCode.trim() }));
    toast.success(`Cost code set on ${selected.size} row(s)`);
  };
  const bulkDuplicate = () => {
    const idxs = [...selected].sort((a, b) => a - b);
    let next = items.slice();
    let offset = 0;
    idxs.forEach((idx) => {
      const at = idx + offset + 1;
      next = [...next.slice(0, at), { ...next[idx + offset] }, ...next.slice(at)];
      offset += 1;
    });
    setItems(next);
    clearSelection();
  };
  const bulkDelete = () => {
    if (!window.confirm(`Delete ${selected.size} selected line(s)?`)) return;
    setItems(items.filter((_, i) => !selected.has(i)));
    clearSelection();
  };

  const onCellKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const { row, cell } = e.currentTarget.dataset;
    const next = tableRef.current?.querySelector(`[data-row="${Number(row) + 1}"][data-cell="${cell}"]`);
    if (next) next.focus();
  };

  const nonSectionCount = items.filter((i) => !i.is_section).length;

  return (
    <div className="min-h-screen flex flex-col" data-testid="quote-item-workspace">
      {/* mobile guard — desktop/landscape tablet only */}
      <div className="lg:hidden flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="item-workspace-mobile-guard">
        <Layers className="w-8 h-8 text-zinc-300 mb-3" />
        <div className="font-display font-black uppercase tracking-tight text-lg">Desktop workspace</div>
        <p className="text-sm text-zinc-500 mt-1 max-w-xs">The full-screen line item grid needs a bigger screen. Use the standard quote editor here instead.</p>
        <Button onClick={() => navigate(`/app/quotes/${qid}`)} className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="item-workspace-mobile-back">
          Back to Quote
        </Button>
      </div>

      <div className="hidden lg:flex flex-col min-h-screen">
        {/* ===== sticky toolbar ===== */}
        <div className="sticky top-0 z-30 border-b border-zinc-200 dark:border-white/10 bg-white/95 dark:bg-[#0B0F14]/95 backdrop-blur px-4 py-2.5 space-y-2.5" data-testid="item-workspace-toolbar">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 min-w-0">
              <button onClick={() => navigate(`/app/quotes/${qid}`)} className="text-zinc-400 hover:text-[#FF5F15] shrink-0" data-testid="item-workspace-back">
                <ArrowLeft className="w-4.5 h-4.5" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-display font-black uppercase tracking-tight text-lg truncate">
                    {isNewDraft(q) ? "Create Quote" : q.quote_number} — Line Items
                  </h1>
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0 ${STATUS_STYLE[status] || STATUS_STYLE.draft}`} data-testid="item-workspace-status">{status}</span>
                </div>
                <div className="text-[11px] text-zinc-400 flex items-center gap-1.5" data-testid="item-workspace-autosave">
                  {saveState === "saved" ? <><Check className="w-3 h-3 text-emerald-500" /> All changes saved</>
                    : saveState === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                    : "Unsaved changes"}
                  <span className="text-zinc-300 dark:text-zinc-600">·</span> {nonSectionCount} line item{nonSectionCount === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/app/quotes/${qid}`)} className="h-8 rounded-sm uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15" data-testid="item-workspace-minimize">
              <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Exit Full Screen
            </Button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* view tabs */}
            <div className="flex border border-zinc-300 dark:border-white/15 rounded-sm overflow-hidden" data-testid="item-workspace-views">
              {VIEWS.map((v) => (
                <button key={v.key} onClick={() => setView(v.key)}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${view === v.key ? "bg-[#FF5F15] text-white" : "bg-white dark:bg-transparent text-zinc-500 hover:text-[#FF5F15]"}`}
                  data-testid={`item-workspace-view-${v.key}`}>
                  {v.label}
                </button>
              ))}
            </div>

            <div className="relative w-56">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="pl-7 h-8 rounded-sm text-xs" data-testid="item-workspace-search" />
            </div>

            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-40 h-8 rounded-sm text-xs" data-testid="item-workspace-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="flex-1" />

            {editable && (
              <>
                <Button size="sm" variant="outline" onClick={() => setLibraryOpen(true)} className="h-8 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="item-workspace-add-from-library">
                  <Library className="w-3 h-3 mr-1.5" /> Rate Library
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase tracking-wider" data-testid="item-workspace-add-menu">
                      <Plus className="w-3 h-3 mr-1.5" /> Add <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-sm">
                    <DropdownMenuItem onClick={() => addItem()} data-testid="item-workspace-add-line">Add Line Item</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addItem({ category: "labour", unit: "hr" })}><Hammer className="w-3.5 h-3.5 mr-2" /> Add Labour</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addItem({ category: "material" })}><Package className="w-3.5 h-3.5 mr-2" /> Add Material</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => addItem({ category: "plant", unit: "hr" })}><Truck className="w-3.5 h-3.5 mr-2" /> Add Plant / Equipment</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={addSection} data-testid="item-workspace-add-section"><Layers className="w-3.5 h-3.5 mr-2" /> Add Section</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {/* bulk edit bar */}
          {selected.size > 0 && editable && (
            <div className="flex items-center gap-2 flex-wrap bg-orange-50 dark:bg-[#FF5F15]/10 border border-orange-200 dark:border-[#FF5F15]/20 rounded-sm px-3 py-2" data-testid="item-workspace-bulk-bar">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#FF5F15]">{selected.size} selected</span>
              <Input type="number" step="0.1" value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} placeholder="%" className="w-16 h-7 rounded-sm text-xs" data-testid="item-workspace-bulk-pct" />
              <Button size="sm" variant="outline" onClick={bulkMarkup} className="h-7 rounded-sm text-[10px] font-bold uppercase" data-testid="item-workspace-bulk-markup">Adjust Sell %</Button>
              <Button size="sm" variant="outline" onClick={bulkDiscount} className="h-7 rounded-sm text-[10px] font-bold uppercase" data-testid="item-workspace-bulk-discount">Set Discount %</Button>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger className="w-36 h-7 rounded-sm text-xs" data-testid="item-workspace-bulk-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={bulkApplyCategory} className="h-7 rounded-sm text-[10px] font-bold uppercase" data-testid="item-workspace-bulk-apply-category">Apply</Button>
              <Input value={bulkCostCode} onChange={(e) => setBulkCostCode(e.target.value)} placeholder="Cost code" className="w-28 h-7 rounded-sm text-xs" data-testid="item-workspace-bulk-costcode" />
              <Button size="sm" variant="outline" onClick={bulkApplyCostCode} className="h-7 rounded-sm text-[10px] font-bold uppercase" data-testid="item-workspace-bulk-apply-costcode">Apply</Button>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={bulkDuplicate} className="h-7 rounded-sm text-[10px] font-bold uppercase" data-testid="item-workspace-bulk-duplicate"><Copy className="w-3 h-3 mr-1" /> Duplicate</Button>
              <Button size="sm" variant="outline" onClick={bulkDelete} className="h-7 rounded-sm text-[10px] font-bold uppercase text-red-600 border-red-300 hover:bg-red-50" data-testid="item-workspace-bulk-delete"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
              <button onClick={clearSelection} className="text-zinc-400 hover:text-zinc-700" data-testid="item-workspace-bulk-clear"><X className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {/* ===== grid ===== */}
        <div ref={tableRef} className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="p-16 text-center text-sm text-zinc-400" data-testid="item-workspace-empty">
              No line items yet — hit <b>+ Add</b> or pull from the <b>Rate Library</b>.
            </div>
          ) : (
            <Grid
              view={view} visible={visible} selected={selected} toggleSelect={toggleSelect}
              updateItem={updateItem} removeItem={removeItem} duplicateItem={duplicateItem}
              sectionSubtotals={sectionSubtotals} dragRef={dragRef} onDrop={onDrop} editable={editable}
              onCellKeyDown={onCellKeyDown}
            />
          )}
        </div>

        {/* ===== totals strip ===== */}
        <div className="sticky bottom-0 z-20 border-t-2 border-zinc-300 dark:border-white/15 bg-white/97 dark:bg-[#0B0F14]/97 backdrop-blur px-4 py-2.5 flex items-center justify-end gap-6 text-xs font-mono" data-testid="item-workspace-totals-strip">
          <div><span className="text-zinc-400 uppercase text-[10px] font-sans font-bold tracking-wider mr-1.5">Subtotal</span>{nzd(totals.subtotal)}</div>
          {totals.discount > 0 && <div><span className="text-zinc-400 uppercase text-[10px] font-sans font-bold tracking-wider mr-1.5">Discount</span>-{nzd(totals.discount)}</div>}
          <div><span className="text-zinc-400 uppercase text-[10px] font-sans font-bold tracking-wider mr-1.5">GST</span>{nzd(totals.gst)}</div>
          <div className="text-base font-bold" data-testid="item-workspace-grand-total"><span className="text-zinc-400 uppercase text-[10px] font-sans font-bold tracking-wider mr-1.5">Total</span>{nzd(totals.total)}</div>
        </div>
      </div>

      <ItemLibraryDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} onAdd={(picked) => { setItems([...items, ...picked]); setLibraryOpen(false); toast.success(`${picked.length} item(s) added`); }} />
    </div>
  );
}

function Grid({ view, visible, selected, toggleSelect, updateItem, removeItem, duplicateItem, sectionSubtotals, dragRef, onDrop, editable, onCellKeyDown }) {
  let n = 0;
  return (
    <table className="w-full text-sm border-collapse" data-testid="item-workspace-grid">
      <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-[#0F141C]">
        <tr className="text-left text-[9px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          <th className="w-8" />
          <th className="w-8" />
          <th className="px-1.5 py-2 w-8">#</th>
          <th className="px-1.5 py-2 min-w-[260px]">Description</th>
          {view === "sell" && (<>
            <th className="px-1.5 py-2 w-20 text-right">Qty</th>
            <th className="px-1.5 py-2 w-16">Unit</th>
            <th className="px-1.5 py-2 w-28 text-right">Unit Price</th>
            <th className="px-1.5 py-2 w-20 text-right">Disc %</th>
            <th className="px-1.5 py-2 w-28 text-right">Total</th>
          </>)}
          {view === "costing" && (<>
            <th className="px-1.5 py-2 w-20 text-right">Qty</th>
            <th className="px-1.5 py-2 w-24">Cost Code</th>
            <th className="px-1.5 py-2 w-28">Category</th>
            <th className="px-1.5 py-2 w-24 text-right">Labour $</th>
            <th className="px-1.5 py-2 w-24 text-right">Material $</th>
            <th className="px-1.5 py-2 w-24 text-right">Plant $</th>
            <th className="px-1.5 py-2 w-28 text-right">Total Cost</th>
          </>)}
          {view === "margin" && (<>
            <th className="px-1.5 py-2 w-24 text-right">Sell Total</th>
            <th className="px-1.5 py-2 w-24 text-right">Cost Total</th>
            <th className="px-1.5 py-2 w-24 text-right">Margin $</th>
            <th className="px-1.5 py-2 w-20 text-right">Margin %</th>
          </>)}
          <th className="w-10" />
        </tr>
      </thead>
      <tbody>
        {visible.map(({ it, i }) => {
          if (it.is_section) {
            return (
              <tr key={i} className="bg-zinc-100 dark:bg-white/[0.06] border-b border-zinc-200 dark:border-white/10"
                draggable={editable} onDragStart={() => { dragRef.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                data-testid={`item-workspace-section-${i}`}>
                <td />
                <td className="pl-2 cursor-grab text-zinc-400"><GripVertical className="w-3.5 h-3.5" /></td>
                <td colSpan={2} className="px-1.5 py-2">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => updateItem(i, { collapsed: !it.collapsed })} className="text-zinc-500" data-testid={`item-workspace-section-collapse-${i}`}>
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${it.collapsed ? "" : "rotate-90"}`} />
                    </button>
                    <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                      className="h-7 rounded-sm border-transparent bg-transparent font-bold uppercase tracking-wider text-xs w-64 focus:border-zinc-300"
                      data-testid={`item-workspace-section-name-${i}`} disabled={!editable} />
                  </div>
                </td>
                <td colSpan={5} />
                <td className="px-1.5 py-2 text-right font-mono font-bold text-xs" data-testid={`item-workspace-section-subtotal-${i}`}>{nzd(sectionSubtotals[i] || 0)}</td>
                <td className="pr-2 text-right">
                  {editable && (
                    <button onClick={() => removeItem(i)} className="p-1 text-zinc-400 hover:text-red-600" data-testid={`item-workspace-section-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            );
          }
          n += 1;
          const rowIdx = n;
          const sell = lineTotal(it);
          const cost = costTotal(it);
          const margin = sell - cost;
          const marginPct = sell > 0 ? (margin / sell) * 100 : 0;
          return (
            <tr key={i} className="border-b border-zinc-100 dark:border-white/5 hover:bg-zinc-50 dark:hover:bg-white/[0.02]"
              draggable={editable} onDragStart={() => { dragRef.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              data-testid={`item-workspace-row-${i}`}>
              <td className="pl-2">
                <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelect(i)} className="w-3.5 h-3.5 accent-[#FF5F15]" data-testid={`item-workspace-select-${i}`} />
              </td>
              <td className="cursor-grab text-zinc-300 dark:text-zinc-600"><GripVertical className="w-3.5 h-3.5" /></td>
              <td className="px-1.5 py-1 font-mono text-xs text-zinc-400">{rowIdx}</td>
              <td className="px-1.5 py-1">
                <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                  onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="description"
                  placeholder="Description" className={`${cellIn} w-full font-medium`} data-testid={`item-workspace-desc-${i}`} disabled={!editable} />
              </td>
              {view === "sell" && (<>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.qty ?? ""} onChange={(e) => updateItem(i, { qty: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="qty" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-qty-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input value={it.unit || ""} onChange={(e) => updateItem(i, { unit: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="unit" placeholder="ea" className={`${cellIn} w-full`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.unit_price ?? ""} onChange={(e) => updateItem(i, { unit_price: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="unit_price" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-price-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input type="number" step="1" min="0" max="100" value={it.discount_pct ?? ""} onChange={(e) => updateItem(i, { discount_pct: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="discount_pct" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-disc-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1 text-right font-mono font-bold text-xs" data-testid={`item-workspace-total-${i}`}>{nzd(sell)}</td>
              </>)}
              {view === "costing" && (<>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.qty ?? ""} onChange={(e) => updateItem(i, { qty: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="qty" className={`${cellIn} w-full text-right`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input value={it.cost_code || ""} onChange={(e) => updateItem(i, { cost_code: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="cost_code" className={`${cellIn} w-full`} data-testid={`item-workspace-costcode-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1">
                  <Select value={it.category || "none"} onValueChange={(v) => updateItem(i, { category: v === "none" ? "" : v })}>
                    <SelectTrigger className={`${cellIn} w-full`} data-testid={`item-workspace-category-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.labour_cost ?? ""} onChange={(e) => updateItem(i, { labour_cost: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="labour_cost" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-labour-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.material_cost ?? ""} onChange={(e) => updateItem(i, { material_cost: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="material_cost" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-material-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1"><Input type="number" step="0.01" value={it.plant_cost ?? ""} onChange={(e) => updateItem(i, { plant_cost: e.target.value })} onKeyDown={onCellKeyDown} data-row={rowIdx} data-cell="plant_cost" className={`${cellIn} w-full text-right`} data-testid={`item-workspace-plant-${i}`} disabled={!editable} /></td>
                <td className="px-1.5 py-1 text-right font-mono font-bold text-xs" data-testid={`item-workspace-costtotal-${i}`}>{nzd(cost)}</td>
              </>)}
              {view === "margin" && (<>
                <td className="px-1.5 py-1 text-right font-mono text-xs" data-testid={`item-workspace-margin-sell-${i}`}>{nzd(sell)}</td>
                <td className="px-1.5 py-1 text-right font-mono text-xs" data-testid={`item-workspace-margin-cost-${i}`}>{nzd(cost)}</td>
                <td className={`px-1.5 py-1 text-right font-mono font-bold text-xs ${margin < 0 ? "text-red-600" : "text-emerald-600"}`} data-testid={`item-workspace-margin-amt-${i}`}>{nzd(margin)}</td>
                <td className={`px-1.5 py-1 text-right font-mono font-bold text-xs ${marginPct < 0 ? "text-red-600" : "text-emerald-600"}`} data-testid={`item-workspace-margin-pct-${i}`}>{marginPct.toFixed(1)}%</td>
              </>)}
              <td className="pr-2 text-right">
                {editable && (
                  <RowMenu i={i} onDup={() => duplicateItem(i)} onDel={() => removeItem(i)} />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RowMenu({ i, onDup, onDel }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1 text-zinc-400 hover:text-zinc-800 dark:hover:text-white" data-testid={`item-workspace-menu-${i}`}>
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm">
        <DropdownMenuItem onClick={onDup} data-testid={`item-workspace-dup-${i}`}><Copy className="w-3.5 h-3.5 mr-2" /> Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDel} className="text-red-600" data-testid={`item-workspace-del-${i}`}><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
